import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { getApiErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatLevel, formatPoints, formatProfession } from "../lib/display";
import { 
  CheckCircle2, 
  Loader2, 
  Plus, 
  Trash2, 
  ShoppingCart,
  User,
  AlertCircle
} from "lucide-react";

const defaultPointsConfig = {
  reais_por_ponto: 50,
  casas_decimais: 2,
};

const QRSalePage = () => {
  const { codigoParceiro } = useParams();
  const [parceiro, setParceiro] = useState(null);
  const [pointsConfig, setPointsConfig] = useState(defaultPointsConfig);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pontosGanhos, setPontosGanhos] = useState(0);
  const [produtos, setProdutos] = useState([{ produto: "", valor: "" }]);

  useEffect(() => {
    fetchParceiro();
  }, [codigoParceiro]);

  const fetchParceiro = async () => {
    try {
      const [parceiroRes, configRes] = await Promise.all([
        api.get(`/parceiros/codigo/${codigoParceiro}`),
        api.get("/points/config").catch(() => ({ data: defaultPointsConfig })),
      ]);
      setParceiro(parceiroRes.data);
      setPointsConfig(configRes.data || defaultPointsConfig);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Parceiro não encontrado."));
    } finally {
      setLoading(false);
    }
  };

  const addProduto = () => {
    setProdutos([...produtos, { produto: "", valor: "" }]);
  };

  const removeProduto = (index) => {
    if (produtos.length > 1) {
      setProdutos(produtos.filter((_, i) => i !== index));
    }
  };

  const updateProduto = (index, field, value) => {
    const updated = [...produtos];
    updated[index][field] = value;
    setProdutos(updated);
  };

  const calcularTotal = () => {
    return produtos.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0);
  };

  const calcularPontosEstimados = () => {
    const reaisPorPonto = Number(pointsConfig?.reais_por_ponto) || defaultPointsConfig.reais_por_ponto;
    const casasDecimais = Math.max(0, Math.min(Number(pointsConfig?.casas_decimais ?? 2), 6));
    const fator = 10 ** casasDecimais;
    return Math.round((calcularTotal() / reaisPorPonto) * fator) / fator;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validProdutos = produtos.filter(p => p.produto && p.valor);
    if (validProdutos.length === 0) {
      toast.error("Adicione pelo menos um produto");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/qr/vendas/${codigoParceiro}`, {
        valor_total: calcularTotal(),
        produtos: validProdutos.map(p => ({
          produto: p.produto,
          valor: parseFloat(p.valor)
        }))
      });
      
      setPontosGanhos(response.data.pontos_gerados);
      setSuccess(true);
      toast.success("Venda registrada com sucesso!");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao registrar venda."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!parceiro) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            Parceiro não encontrado
          </h1>
          <p className="text-slate-600">
            O código {codigoParceiro} não está cadastrado.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center animate-slide-up">
          <div className="h-20 w-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-success" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            Venda Registrada!
          </h1>
          <p className="text-slate-600 mb-4">
            O parceiro <strong>{parceiro.nome}</strong> ganhou
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 rounded-full">
            <span className="text-3xl font-heading font-bold text-primary">
              +{formatPoints(pontosGanhos)}
            </span>
            <span className="text-primary font-medium">pontos</span>
          </div>
          <p className="mt-6 text-sm text-slate-500">
            Obrigado por indicar um cliente!
          </p>
          <Button
            onClick={() => {
              setSuccess(false);
              setProdutos([{ produto: "", valor: "" }]);
            }}
            className="mt-6 btn-primary"
            data-testid="new-sale-btn"
          >
            Registrar nova venda
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-primary text-white px-4 py-6">
        <div className="max-w-lg mx-auto">
          <h1 className="font-heading text-xl font-bold">Registro de Venda</h1>
          <p className="text-blue-100 text-sm mt-1">via QR Code</p>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Partner info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-7 w-7 text-primary" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Parceiro</p>
            <p className="font-heading font-semibold text-lg text-slate-900">
              {parceiro.nome}
            </p>
            <p className="text-sm text-slate-600">{formatProfession(parceiro.profissao)}</p>
          </div>
          <div className="ml-auto">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
              parceiro.nivel === "Ouro" 
                ? "level-badge-gold" 
                : parceiro.nivel === "Prata" 
                  ? "level-badge-silver" 
                  : "level-badge-bronze"
            }`}>
              {formatLevel(parceiro.nivel)}
            </span>
          </div>
        </div>

        {/* Sale form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
              Produtos da venda
            </h2>

            <div className="space-y-4">
              {produtos.map((produto, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-1">
                    <Label htmlFor={`produto-${index}`} className="sr-only">
                      Produto
                    </Label>
                    <Input
                      id={`produto-${index}`}
                      placeholder="Nome do produto"
                      value={produto.produto}
                      onChange={(e) => updateProduto(index, "produto", e.target.value)}
                      className="input-field"
                      data-testid={`produto-input-${index}`}
                    />
                  </div>
                  <div className="w-28">
                    <Label htmlFor={`valor-${index}`} className="sr-only">
                      Valor
                    </Label>
                    <Input
                      id={`valor-${index}`}
                      type="number"
                      step="0.01"
                      placeholder="R$ 0,00"
                      value={produto.valor}
                      onChange={(e) => updateProduto(index, "valor", e.target.value)}
                      className="input-field"
                      data-testid={`valor-input-${index}`}
                    />
                  </div>
                  {produtos.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeProduto(index)}
                      className="text-slate-400 hover:text-red-500"
                      data-testid={`remove-produto-btn-${index}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={addProduto}
              className="w-full mt-4"
              data-testid="add-produto-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar produto
            </Button>
          </div>

          {/* Total */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Total da venda</span>
              <span className="font-heading text-2xl font-bold text-slate-900">
                R$ {calcularTotal().toFixed(2)}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Pontos estimados: ~{formatPoints(calcularPontosEstimados())} pontos
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full h-14"
            data-testid="submit-sale-btn"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <ShoppingCart className="h-5 w-5 mr-2" />
                Registrar Venda
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default QRSalePage;
